import { formatTableLabel } from "./dineInBoardBuilder";

// ─── Dine In board — QR download ──────────────────────────────────────────
// Composites a rendered QR canvas into a branded, downloadable card. Same
// approach as ShareProfileModal.getQrFile: draw the live canvas onto a bigger
// one, add text around it, export a PNG.

/** Card geometry, in px, at the scale the QR is handed to us. */
const PADDING = 64;
const HEADER_HEIGHT = 190;
const FOOTER_HEIGHT = 96;
const LOGO_SIZE = 84;

/**
 * Loads the business logo for compositing, or resolves null.
 *
 * crossOrigin="anonymous" is mandatory, not defensive. Drawing an image from
 * another origin without CORS permission TAINTS the canvas, and a tainted
 * canvas throws SecurityError on toBlob — meaning one un-CORSed logo wouldn't
 * degrade the download, it would kill it entirely. Requesting anonymously
 * means the load fails cleanly instead, and we simply draw the card without a
 * logo.
 *
 * The timeout covers the other failure mode: a storage host that accepts the
 * connection and then never answers, which would otherwise hang the download
 * forever with no feedback.
 */
function loadLogo(url: string | undefined): Promise<HTMLImageElement | null> {
  if (!url) return Promise.resolve(null);

  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), 4000);
    const settle = (result: HTMLImageElement | null) => {
      clearTimeout(timer);
      resolve(result);
    };

    img.crossOrigin = "anonymous";
    img.onload = () => settle(img);
    img.onerror = () => settle(null);
    img.src = url;
  });
}

/** Shrinks text until it fits, rather than letting it run off the card. */
function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  weight: string,
  startPx: number
): void {
  let px = startPx;
  ctx.font = `${weight} ${px}px sans-serif`;
  while (ctx.measureText(text).width > maxWidth && px > 12) {
    px -= 2;
    ctx.font = `${weight} ${px}px sans-serif`;
  }
}

export interface TableQrDownloadOptions {
  /** The already-rendered QR canvas — Nine's logo is baked into it. */
  qrCanvas: HTMLCanvasElement;
  businessName: string;
  businessLogoUrl?: string;
  tableNumber: number;
}

/**
 * Builds the card and triggers a download.
 *
 * The QR is drawn from the caller's canvas rather than regenerated, so what
 * lands in the file is exactly what was on screen — including the Nine logo
 * and its error correction. Regenerating here would risk the two drifting.
 */
export async function downloadTableQrPng({
  qrCanvas,
  businessName,
  businessLogoUrl,
  tableNumber,
}: TableQrDownloadOptions): Promise<boolean> {
  const logo = await loadLogo(businessLogoUrl);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;

  canvas.width = qrCanvas.width + PADDING * 2;
  canvas.height = qrCanvas.height + PADDING * 2 + HEADER_HEIGHT + FOOTER_HEIGHT;
  const centreX = canvas.width / 2;
  const maxTextWidth = canvas.width - PADDING * 2;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.textAlign = "center";

  let y = PADDING;

  // Restaurant logo, clipped to a circle. Skipped silently when it couldn't be
  // loaded under CORS — see loadLogo.
  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(centreX, y + LOGO_SIZE / 2, LOGO_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logo, centreX - LOGO_SIZE / 2, y, LOGO_SIZE, LOGO_SIZE);
    ctx.restore();
    y += LOGO_SIZE + 26;
  } else {
    y += 20;
  }

  ctx.fillStyle = "#475569";
  fitText(ctx, businessName, maxTextWidth, "600", 30);
  ctx.fillText(businessName, centreX, y);
  y += 46;

  const tableLabel = `TABLE ${formatTableLabel(tableNumber).replace(/^T/, "")}`;
  ctx.fillStyle = "#0f172a";
  fitText(ctx, tableLabel, maxTextWidth, "800", 46);
  ctx.fillText(tableLabel, centreX, y);

  ctx.drawImage(qrCanvas, centreX - qrCanvas.width / 2, PADDING + HEADER_HEIGHT);

  ctx.fillStyle = "#64748b";
  ctx.font = "500 26px sans-serif";
  ctx.fillText(
    "Scan to order from your phone",
    centreX,
    canvas.height - PADDING - 12
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png", 1)
  );
  if (!blob) return false;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const slug = businessName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-") || "nine";
  link.download = `${slug}-table-${tableNumber}-qr.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick, not immediately: Safari cancels an in-flight
  // download if the object URL disappears in the same frame as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return true;
}

export default { downloadTableQrPng };
